import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import {
  PlanPriceListResponse,
  PlanPriceShowResponse,
} from '../../../Interfaces/api/backoffice/plan.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import { PlanComponent } from '../../../Models/backoffice/plan-component.model';
import { PlanPrice } from '../../../Models/backoffice/plan-price.model';
import { PlanPricePayload } from '../Interfaces/plan-filters.interface';

@Injectable({
  providedIn: 'root',
})
export class PlanPriceService {
  private readonly _apiService = inject(ApiService);

  listRequest(
    planId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<PlanPriceListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PlanPriceListResponse>>(
      endpoints.backoffice.planPrices(planId),
      { skipLoading: true },
    );
  }

  create(planId: string, payload: PlanPricePayload): Observable<PlanPrice | null> {
    const request = this._apiService.post<LaravelDefaultResponse<PlanPriceShowResponse>>(
      endpoints.backoffice.planPrices(planId),
      { body: payload, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<PlanPriceShowResponse>>(request)
      .pipe(map((response) => this._toPrice(response)));
  }

  update(
    planId: string,
    priceId: string,
    payload: Partial<PlanPricePayload>,
  ): Observable<PlanPrice | null> {
    const request = this._apiService.put<LaravelDefaultResponse<PlanPriceShowResponse>>(
      endpoints.backoffice.planPrice(planId, priceId),
      { body: payload, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<PlanPriceShowResponse>>(request)
      .pipe(map((response) => this._toPrice(response)));
  }

  deactivate(planId: string, priceId: string): Observable<PlanPrice | null> {
    const request = this._apiService.delete<LaravelDefaultResponse<PlanPriceShowResponse>>(
      endpoints.backoffice.planPrice(planId, priceId),
      { skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<PlanPriceShowResponse>>(request)
      .pipe(map((response) => this._toPrice(response)));
  }

  toPrices(response: LaravelDefaultResponse<PlanPriceListResponse> | undefined): PlanPrice[] {
    return (response?.data?.prices ?? []).map((row) => new PlanPrice(row));
  }

  toComponents(
    response: LaravelDefaultResponse<PlanPriceListResponse> | undefined,
  ): PlanComponent[] {
    return (response?.data?.components ?? []).map((row) => new PlanComponent(row));
  }

  toNoticeKey(response: LaravelDefaultResponse<PlanPriceListResponse> | undefined): string {
    return response?.data?.notice ?? '';
  }

  private _toPrice(response: LaravelDefaultResponse<PlanPriceShowResponse>): PlanPrice | null {
    return response.data?.price ? new PlanPrice(response.data.price) : null;
  }
}
